<template>
  <div class="verify-email-content">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>{{ t('account.personal_data.btn.saving') }}</p>
    </div>

    <!-- Success State -->
    <div v-else-if="validationResult && validationResult.valid" class="success-state">
      <div class="status-icon status-icon-success mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
      </div>
      <h1 class="status-title">
        {{ successTitle }}
      </h1>
      <p class="redirect-info">
        {{ t('auth.verify.redirect_msg', { count: redirectCountdown }) }}
      </p>
    </div>

    <!-- Error State -->
    <div v-else class="error-state">
      <div class="status-icon status-icon-error mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      </div>
      <h1 class="status-title">
        {{ errorTitle }}
      </h1>
      <div class="error-actions mt-4">
        <router-link to="/countdown" class="btn-back-home">
          {{ t('newsletter.back_to_home') }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { useConfigStore } from '@/stores/configStore'
import { useLanguage } from '@/composables/useLanguage'
import { useApiCodes } from '@/composables/useApiCodes'

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (value) => ['account', 'newsletter'].includes(value)
  }
})

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const configStore = useConfigStore()
const { t } = useLanguage()
const { getErrorCode } = useApiCodes()

const isLoading = ref(true)
const validationResult = ref(null)
const redirectCountdown = ref(5)

const successTitle = computed(() => {
  if (props.type === 'account') {
    return t('auth.verify.success_title')
  }
  return t('newsletter.verify_success')
})

const errorTitle = computed(() => {
  if (props.type === 'account') {
    return t('auth.verify.error_title')
  }
  return t('newsletter.verify_error')
})

watch(() => validationResult.value?.valid, (isValid) => {
  if (isValid) {
    const countdown = setInterval(() => {
      redirectCountdown.value -= 1
      if (redirectCountdown.value <= 0) {
        clearInterval(countdown)
        if (configStore.isCountdownMode) {
          router.push('/countdown')
        } else {
          router.push('/login')
        }
      }
    }, 1000)
  }
})

const generateRecaptchaToken = async (action) => {
  if (!configStore.recaptchaSiteKey) return ''

  const waitForRecaptcha = () => {
    return new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        resolve(true)
        return
      }
      let attempts = 0
      const interval = setInterval(() => {
        attempts++
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          clearInterval(interval)
          resolve(true)
        } else if (attempts > 50) {
          clearInterval(interval)
          resolve(false)
        }
      }, 100)
    })
  }

  const isRecaptchaReady = await waitForRecaptcha()
  if (!isRecaptchaReady) return ''

  return await new Promise((resolve) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(configStore.recaptchaSiteKey, { action })
        resolve(token)
      } catch (e) {
        console.error('[RECAPTCHA] Execute error:', e)
        resolve('')
      }
    })
  })
}

const verifyToken = async () => {
  isLoading.value = true
  validationResult.value = null

  try {
    const tokenParam = route.query.token || route.params.token

    if (!tokenParam) {
      validationResult.value = {
        valid: false,
        message: errorTitle.value
      }
      return
    }

    try {
      if (props.type === 'account') {
        const recaptchaToken = await generateRecaptchaToken('verify_email')
        if (!recaptchaToken) {
          validationResult.value = {
            valid: false,
            message: t('apiCodes.AUTH_RECAPTCHA_REQUIRED')
          }
          return
        }
        await authStore.verifyEmail('', tokenParam, recaptchaToken)
      } else {
        await authStore.verifyNewsletter(tokenParam)
      }
      validationResult.value = {
        valid: true,
        message: successTitle.value
      }
    } catch (apiError) {
      const code = getErrorCode(apiError)
      const message = code === 'AUTH_RECAPTCHA_REQUIRED' || code === 'AUTH_RECAPTCHA_FAILED'
        ? t('apiCodes.' + code)
        : errorTitle.value
      validationResult.value = {
        valid: false,
        message
      }
    }
  } catch (err) {
    console.error('[VERIFY EMAIL] Error:', err)
    validationResult.value = {
      valid: false,
      message: errorTitle.value
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  verifyToken()
})
</script>

<style scoped>
.verify-email-content {
  width: 100%;
  text-align: center;
}

.loading-state, .success-state, .error-state {
  animation: rise 0.5s cubic-bezier(0.16,1,0.3,1);
}

@keyframes rise {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

.spinner {
  width: 40px;
  height: 40px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid var(--dark-teal);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.status-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin-bottom: 20px;
}

.status-icon-success {
  color: var(--dark-teal);
  background: rgba(0,140,149,0.08);
}

.status-icon-error {
  color: #d9534f;
  background: rgba(217,83,79,0.08);
}

.status-title {
  font-family: 'Montserrat', sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #000000;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.4;
}

.redirect-info {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #666666;
  margin-bottom: 24px;
  line-height: 1.6;
}

.btn-back-home {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 15px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  background: #000000;
  border: none;
  border-radius: 4px;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
}

.btn-back-home:hover {
  background: var(--dark-teal);
  box-shadow: 0 8px 20px -6px rgba(0,140,149,0.5);
  color: #ffffff;
  transform: translateY(1px);
}

@media (max-width: 600px) {
  .status-title {
    font-size: 18px;
  }
}
</style>
